import type React from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Printer, X } from "lucide-react";
import { useBiens } from "@/hooks/useBiens";
import { useProfiles } from "@/hooks/useProfiles";
import type { Bien } from "@/types/bien";
import { useColumnPreferences } from "@/hooks/useColumnPreferences";
import { getOrderedVisibleColumns, type BiensColumnKey } from "@/lib/biensColumns";
import { formatDateShort } from "@/lib/today";
import { computeStatutEls } from "@/lib/elsStatus";

/**
 * Vue imprimable de la liste des biens — pensée pour la prep réunion CEO.
 *
 * Reprend l'ordre + les filtres actifs depuis BiensListPage via localStorage
 * ("biens-print-ids" : ids triés filtrés). Le user clique "Exporter PDF" sur
 * /biens, on ouvre cette route dans un nouvel onglet, il imprime ou sauve en
 * PDF via le navigateur (Cmd+P → Save as PDF).
 *
 * Format A : table simple à 9 colonnes, mêmes infos que la liste écran +
 * "Dispo depuis X jours" en alerte couleur. Le user décide de la sélection
 * en filtrant à l'écran avant d'exporter.
 */

const today = new Date();

function formatHeaderDate(d: Date): string {
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

interface DispoDepuisInfo {
  days: number;
  level: "neuf" | "enAttente" | "alerte" | "critique";
}

function getDispoDepuis(disponibilite: string, seraDispoPour: string): DispoDepuisInfo | null {
  if ((disponibilite || "").toUpperCase() !== "DISPONIBLE") return null;
  if (!seraDispoPour) return null;
  const d = new Date(seraDispoPour);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  if (d > now) return null;
  const days = Math.max(0, Math.floor((now.getTime() - d.getTime()) / 86400000));
  if (days >= 180) return { days, level: "critique" };
  if (days >= 90) return { days, level: "alerte" };
  if (days >= 30) return { days, level: "enAttente" };
  return { days, level: "neuf" };
}

export function BiensPrintPage() {
  const { t } = useTranslation();
  const { data: biensData } = useBiens();
  const allBiens = useMemo(() => (biensData ?? []) as unknown as Bien[], [biensData]);
  const { data: profiles = [] } = useProfiles();

  const collabNameMap = useMemo(() => {
    const m: Record<string, string> = {};
    profiles.forEach((p) => {
      m[p.id] = `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || p.email;
    });
    return m;
  }, [profiles]);

  // IDs passés depuis BiensListPage via localStorage (ordre filtre+tri préservé)
  const storedIds: string[] = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("biens-print-ids") || "[]");
    } catch {
      return [];
    }
  }, []);

  // Sélection des colonnes : Supabase si user authentifié (cf. useColumn-
  // Preferences), sinon localStorage en fallback. La sélection est mirror
  // dans localStorage à chaque save → la print page (nouvel onglet) la lit
  // immédiatement sans attendre le re-fetch du profile.
  const { columns: visibleColumns } = useColumnPreferences();
  const orderedColumns = useMemo(() => getOrderedVisibleColumns(visibleColumns), [visibleColumns]);

  const printBiens = useMemo(() => {
    if (storedIds.length === 0) return allBiens;
    const byId = new Map(allBiens.map((b) => [b.id, b]));
    return storedIds.map((id) => byId.get(id)).filter(Boolean) as typeof allBiens;
  }, [allBiens, storedIds]);

  // L'utilisateur clique manuellement le bouton "Imprimer / Sauver en PDF" :
  // évite un dialog auto-bloquant si l'onglet est ouvert pour un coup d'oeil
  // sans intention d'imprimer.

  const th =
    "border border-gray-400 px-2 py-1 text-left text-[9px] font-bold uppercase tracking-wide bg-gray-100 whitespace-nowrap";
  const td = "border border-gray-300 px-2 py-1 whitespace-nowrap";

  const dispoDepuisStyle = (level: DispoDepuisInfo["level"]): React.CSSProperties => {
    if (level === "critique") return { color: "#b91c1c", fontWeight: 600 };
    if (level === "alerte") return { color: "#c2410c", fontWeight: 500 };
    if (level === "enAttente") return { color: "#b45309" };
    return { color: "#6b7280" };
  };

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { margin: 8mm; size: A4 landscape; }
          body { font-size: 8pt; }
          thead { display: table-header-group; }
        }
      `}</style>

      <div
        className="bg-white min-h-screen text-black"
        style={{ fontFamily: "Arial, Helvetica, sans-serif", fontSize: "10px" }}
      >
        {/* Toolbar — masquée à l'impression */}
        <div className="no-print sticky top-0 bg-gray-50 border-b border-gray-200 px-6 py-2 flex items-center gap-3 z-20 shadow-sm">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 px-4 py-1.5 bg-gray-900 text-white rounded text-sm font-medium hover:bg-gray-700 cursor-pointer transition-colors"
          >
            <Printer size={14} />
            {t("biens.print.exportPdf")}
          </button>
          <button
            onClick={() => window.close()}
            className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 text-gray-600 rounded text-sm font-medium hover:bg-gray-50 cursor-pointer transition-colors"
          >
            <X size={14} />
            {t("biens.print.close")}
          </button>
          <span className="ml-auto text-sm text-gray-500">
            {printBiens.length} {t("biens.print.subtitle")} — {formatHeaderDate(today)}
          </span>
        </div>

        <div className="p-8" style={{ maxWidth: "1600px", margin: "0 auto" }}>
          {/* Header */}
          <div className="mb-4">
            <h1
              style={{
                fontSize: "18px",
                fontWeight: "bold",
                textAlign: "center",
                marginBottom: "4px",
              }}
            >
              {t("biens.print.title")}
            </h1>
            <p style={{ fontSize: "10px", textAlign: "center", color: "#666" }}>
              {formatHeaderDate(today)} — {printBiens.length} {t("biens.print.subtitle")}
            </p>
          </div>

          {/* Table */}
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "9px" }}>
            <thead>
              <tr>
                {orderedColumns.map((col) => {
                  const right = col.align === "right";
                  return (
                    <th key={col.key} className={th} style={right ? { textAlign: "right" } : undefined}>
                      {t(col.i18nLabel)}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {printBiens.map((b, i) => {
                const info = getDispoDepuis(b.disponibilite ?? "", b.seraDispoPour ?? "");
                const bg = i % 2 === 0 ? "#fff" : "#f9f9f9";
                const ynLabel = (v: boolean | null | undefined) => v === true ? t("common.yes") : v === false ? t("common.no") : "—";
                const renderCell = (key: BiensColumnKey) => {
                  switch (key) {
                    case "ref": return <td key={key} className={td} style={{ fontWeight: 600 }}>{b.referenceInterne || "—"}</td>;
                    case "projet": return <td key={key} className={td}>{(b.projet as string | undefined) || "—"}</td>;
                    case "adresse": return <td key={key} className={td}>{b.adresse || "—"}</td>;
                    case "codePostal": return <td key={key} className={td}>{b.codePostal || "—"}</td>;
                    case "ville": return <td key={key} className={td}>{b.ville || "—"}</td>;
                    case "region": return <td key={key} className={td} style={{ textTransform: "capitalize" }}>{b.region || "—"}</td>;
                    case "collaborateur": return <td key={key} className={td}>{collabNameMap[b.collaborateurSrs ?? ""] || "—"}</td>;
                    case "disponibilite": return <td key={key} className={td}>{b.disponibilite || "—"}</td>;
                    case "dispoDepuis": return <td key={key} className={td} style={{ textAlign: "right", ...(info ? dispoDepuisStyle(info.level) : {}) }}>{info ? `${info.days} j` : "—"}</td>;
                    case "seraDispoPour": return <td key={key} className={td}>{formatDateShort(b.seraDispoPour) || "—"}</td>;
                    case "publication": return <td key={key} className={td}>{b.statutPublication || b.publication || "—"}</td>;
                    case "prix": return <td key={key} className={td} style={{ textAlign: "right", fontWeight: 500 }}>{b.loyerMensuel ? `${b.loyerMensuel} €` : b.prix || "—"}</td>;
                    case "charges": return <td key={key} className={td} style={{ textAlign: "right" }}>{b.chargesMensuelles ? `${b.chargesMensuelles} €` : "—"}</td>;
                    case "surface": return <td key={key} className={td} style={{ textAlign: "right" }}>{b.surfaceHabitable ? `${b.surfaceHabitable} m²` : b.surface || "—"}</td>;
                    case "nbChambres": return <td key={key} className={td} style={{ textAlign: "right" }}>{b.nbChambres || "—"}</td>;
                    case "nbSdb": return <td key={key} className={td} style={{ textAlign: "right" }}>{b.nbSdb || "—"}</td>;
                    case "etage": return <td key={key} className={td} style={{ textAlign: "right" }}>{b.etage || "—"}</td>;
                    case "classePeb": return <td key={key} className={td}>{b.classePeb || "—"}</td>;
                    case "pebFinLe": return <td key={key} className={td}>{formatDateShort(b.pebFinLe) || "—"}</td>;
                    case "renonRecuLe": return <td key={key} className={td}>{formatDateShort(b.renonRecuLe) || "—"}</td>;
                    case "finDeContrat": return <td key={key} className={td}>{formatDateShort(b.finDeContrat) || "—"}</td>;
                    case "meuble": return <td key={key} className={td}>{ynLabel(b.meuble)}</td>;
                    case "companyContract": return <td key={key} className={td}>{ynLabel(b.companyContract)}</td>;
                    // Colonne DB supprimée (mig. 112) → statut calculé.
                    case "statutEls": return <td key={key} className={td}>{computeStatutEls(b)}</td>;
                  }
                };
                return (
                  <tr key={b.id} style={{ backgroundColor: bg }}>
                    {orderedColumns.map((col) => renderCell(col.key))}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Footer */}
          <div
            style={{
              marginTop: "16px",
              borderTop: "1px solid #ccc",
              paddingTop: "8px",
              textAlign: "center",
              fontSize: "8px",
              color: "#666",
            }}
          >
            NV/S.A. Skyline Renting Services — Boulevard du Souverain 292 — 1160 Auderghem — 02/679.00.06 — TVA BE 0849.827.193 — info@skylinerenting.eu
          </div>
        </div>
      </div>
    </>
  );
}
